import {
  Comments,
  CurrentComment,
} from 'containers/NewReviewed/modals/CommentsLoadingModal';
import {CSSProperties} from 'react';
import {ReactourStep} from 'reactour';
import {Marker} from 'utils/functions/editor/paragraphs';
import {useMobile} from 'utils/hooks';
import {CompositionMarker} from 'utils/types/compositions';
import {
  CompositionChannelMessage,
  CompositionReviewedStatus,
  Grade,
  NumericMetadata,
  Indicators,
  ShownStat,
} from 'utils/types/newReviewed';

type WebSocketMessage =
  | 'zero_grader'
  | 'plagiarism'
  | 'text_markers'
  | 'grade'
  | 'comments_and_indicators'
  | 'finish';

export interface EditorMarkerParams {
  markers: CompositionMarker[] | Marker[];
  view?: string;
  onMouseEnter?(marker: CompositionMarker): void;
  onMouseLeave?(marker: CompositionMarker): void;
  onClick?(marker: CompositionMarker): void;
  className?: string;
}

interface useNewReviewedParams {
  images: Array<string>;
  websocketMessages?: CompositionChannelMessage[];
  httpResponse?: CompositionReviewedStatus;
}

function useNewReviewed({
  images = [],
  httpResponse,
  websocketMessages,
}: Partial<useNewReviewedParams> = {}) {
  const {isMobile} = useMobile();

  function updateCommentsReviewed(setCommentsReviewed) {
    setCommentsReviewed((prevState: Comments) => {
      const nextComment = prevState.currentComment + 1;

      let nextImage = prevState.currentImage;

      if (nextImage < images.length - 1) {
        nextImage++;
      } else {
        nextImage = prevState.currentImage === 1 ? 2 : 1;
      }

      return {
        currentComment: nextComment,
        currentImage: nextImage,
      };
    });
  }

  function getButtonText(currentComment: number, totalComments: number) {
    if (currentComment === 0) {
      return 'Ver comentários';
    }

    if (currentComment === totalComments - 1) {
      return 'Ver correção';
    }

    return 'Ver próximo';
  }

  function getComparativeFeedback(
    externalReference,
    difference,
    comparisonResult,
  ) {
    const formattedPointsLabel = `${difference} ${
      difference === 1 ? 'ponto' : 'pontos'
    }`;

    if (comparisonResult === 'negative') {
      return {
        status: 'smaller',
        icon: 'bars-progress',
        color: '#F3AF31',
        externalReference,
        comparativeWord: 'abaixo',
        points: formattedPointsLabel,
      };
    }
    if (comparisonResult === 'positive') {
      return {
        status: 'bigger',
        icon: 'ranking-star',
        color: '#2FAC66',
        externalReference,
        comparativeWord: 'acima',
        points: formattedPointsLabel,
      };
    }
    return {
      status: 'equal',
      icon: 'chart-simple',
      color: '#2A5082',
      comparativeWord: 'dentro',
      points: '100%',
    };
  }

  function getMarkerColor(view?: string) {
    if (view === 'positivos') {
      return 'greenMarker';
    }

    if (view === 'atencao') {
      return 'yellowMarker';
    }

    if (view === 'zeramento') {
      return 'redMarker';
    }

    return '';
  }

  function getEditorMarkers({
    markers,
    view,
    onMouseEnter,
    onMouseLeave,
    onClick,
  }: EditorMarkerParams): Marker[] {
    return markers?.map((marker) => ({
      id: marker.id,
      start: marker.start,
      end: marker.end,
      paragraph: marker.paragraph,
      onMouseEnter: () => onMouseEnter?.(marker),
      onMouseLeave: () => onMouseLeave?.(marker),
      onClick: () => onClick?.(marker),
      className: `${marker.className || getMarkerColor(view)}`,
    }));
  }

  function getGreaterMarkers(highlightedMarkers: Marker[]): Marker[] {
    return (
      highlightedMarkers?.filter((highlightedMarker: Marker) => {
        const visibleMarker = Object.values(comments ?? []).some((comment) => {
          return (
            comment.competence_id === highlightedMarker.competence_id &&
            comment.priority === 'greater'
          );
        });

        return visibleMarker;
      }) ?? []
    );
  }

  function getSmallerMarkers(highlightedMarkers: Marker[]): Marker[] {
    return (
      highlightedMarkers?.filter((highlightedMarker: Marker) => {
        const visibleMarker = Object.values(comments ?? []).some((comment) => {
          return (
            comment.competence_id === highlightedMarker.competence_id &&
            comment.priority === 'smaller'
          );
        });

        return visibleMarker;
      }) ?? []
    );
  }

  function getGreaterCompetence(visibleMarkers: Marker[]) {
    const greaterCompetence = Object.entries(comments ?? {})
      .filter(([, comment]) => comment?.priority === 'greater')
      .map(([competence, comment]) => ({
        competence,
        ...comment,
      }));

    const greaterCompetenceMarkers = greaterCompetence.filter(
      ({competence_id}) =>
        visibleMarkers?.some((marker) => marker.id === competence_id),
    );

    return greaterCompetence.length
      ? greaterCompetence
      : greaterCompetenceMarkers;
  }

  function getSmallerCompetence(visibleMarkers: Marker[]) {
    const smallerCompetence = Object.entries(comments ?? {})
      .filter(([, comment]) => comment?.priority === 'smaller')
      .map(([competence, comment]) => ({
        competence,
        ...comment,
      }));

    const smallerCompetenceMarkers = smallerCompetence.filter(
      ({competence_id}) =>
        visibleMarkers?.some((marker) => marker.id === competence_id),
    );

    return smallerCompetence.length
      ? smallerCompetence
      : smallerCompetenceMarkers;
  }

  function getIndicators(indicators?: NumericMetadata) {
    const indicatorsValues = Object.values(indicators ?? {});
    const isEmpty =
      indicators?.palavras?.value === 0 || indicatorsValues.length === 0;

    if (isEmpty) return null;

    return {
      words: indicators?.palavras?.value,
      paragraphs: indicators?.paragrafos?.value,
      dots: indicators?.pontos?.value,
      commas: indicators?.virgulas?.value,
      orthographics_errors: indicators?.erros_ortograficos?.value,
      connectives: indicators?.conectivos?.value,
      oralities: indicators?.oralidades?.value,
      sentences: indicators?.sentencas?.value,
      social_agents: indicators?.agentes_sociais?.value,
      propositionals: indicators?.propositivos?.value,
    };
  }

  function getWebsocketMessage(eventName: WebSocketMessage) {
    const message =
      websocketMessages?.filter((message) => message.name === eventName) ?? [];

    // returns the last message, assuming that it will be the most recent
    return message[message.length - 1];
  }

  function getWebSocketIndicators() {
    const markersMessage = getWebsocketMessage('text_markers');

    return getIndicators(markersMessage?.data?.numeric_metadatas);
  }

  function getWebSocketZeroGrader() {
    const zeroGraderMessage = getWebsocketMessage('zero_grader');
    const plagiarismMessage = getWebsocketMessage('plagiarism');

    return (
      (zeroGraderMessage?.data.is_zeroed ||
        plagiarismMessage?.data.is_zeroed) ??
      null
    );
  }

  function getWebSocketFinish() {
    const finishMessage = getWebsocketMessage('finish');

    return {
      webSocketReviewId: finishMessage?.data?.review_id,
    };
  }

  function getWebSocketComments() {
    const commentsMessage = getWebsocketMessage('comments_and_indicators');

    return commentsMessage?.data as NumericMetadata;
  }

  function getWebSocketMarkers() {
    // get all text_markers messages from the channel
    const markerMessage = getWebsocketMessage('text_markers');

    return markerMessage?.data?.json_metadatas;
  }

  function getWebSocketGrades(): Grade {
    const gradeMessage = getWebsocketMessage('grade');

    return {
      grade: gradeMessage?.data.grade,
      difference: gradeMessage?.data.difference,
      comparison_result: gradeMessage?.data.comparison_result,
    };
  }

  function getShownStats(stats: ShownStat[]) {
    return stats?.map((indicatorStat) => ({
      varname: indicatorStat?.marker?.varname,
      goal: indicatorStat?.goal,
      below_goal: indicatorStat?.below_goal,
      ideal_range_max: indicatorStat?.ideal_range_max,
      ideal_range_min: indicatorStat?.ideal_range_min,
      description: indicatorStat?.description,
      public_name: indicatorStat?.public_name,
    }));
  }

  function getCurrentComments(
    indicators: Indicators | null,
    stats: ShownStat[],
  ) {
    const shownStats = getShownStats(stats)?.reduce(
      (acc, stat) => {
        const key = stat?.varname ?? '';
        acc.goal[key] = stat?.goal;
        acc.belowGoal[key] = stat?.below_goal;
        return acc;
      },
      {
        goal: {palavras: 0, paragrafos: 0, virgulas: 0, pontos: 0},
        belowGoal: {palavras: 0, paragrafos: 0, virgulas: 0, pontos: 0},
      },
    );

    const currentComments: CurrentComment[] = [
      {
        text: 'Estou animada. Um texto novo! 🥳 Enquanto corrigimos, vamos interagir sobre a sua escrita.',
        isQuestion: false,
      },
      {
        text: `Seu texto tem ${
          indicators?.words ?? ''
        } palavras. Você sabia que redações com nota máxima contêm mais de _________ palavras?`,
        isQuestion: true,
        correctAnswer: `${shownStats.goal?.palavras} palavras`,
        incorrectAnswer: `${shownStats.belowGoal?.palavras} palavras`,
      },
      {
        text: `Você escreveu ${
          indicators?.paragraphs ?? ''
        } parágrafos. Redações com nota máxima contêm, em média, ____.`,
        isQuestion: true,
        correctAnswer: `${shownStats.goal?.paragrafos} parágrafos`,
        incorrectAnswer: `${shownStats.belowGoal?.paragrafos} parágrafos`,
      },
      {
        text: `Você usou ${indicators?.commas ?? ''} vírgulas e ${
          indicators?.dots ?? ''
        } pontos, totalizando ${
          (indicators?.commas ?? 0) + (indicators?.dots ?? 0)
        } no seu texto. A meta considerando redações com nota máxima é _____?.`,
        isQuestion: true,
        correctAnswer: `${shownStats.goal?.virgulas + shownStats.goal?.pontos}`,
        incorrectAnswer: `${
          shownStats.belowGoal?.virgulas + shownStats.belowGoal?.pontos
        }`,
      },
      {
        text: 'Já estamos terminando a correção do seu texto, gerando sua nota e criando instruções de melhoria.',
        isQuestion: false,
      },
    ];

    function getQuestionFeedback(questionAnswer: string) {
      const currentCommentCorrectAnswer = currentComments?.find(
        (comment) => comment.correctAnswer === questionAnswer,
      );

      const currentCommentIncorrectAnswer = currentComments?.find(
        (comment) => comment.incorrectAnswer === questionAnswer,
      );

      if (currentCommentCorrectAnswer) {
        return `Isso mesmo! São ${currentCommentCorrectAnswer?.correctAnswer} que as redações com nota máxima geralmente contém.`;
      }

      if (currentCommentIncorrectAnswer) {
        return `Foi quase! São ${currentCommentIncorrectAnswer?.correctAnswer} que as redações com nota máxima geralmente contém.`;
      }

      return '';
    }

    return {currentComments, getQuestionFeedback};
  }

  function getTourSteps() {
    const stepTitleStyle: CSSProperties = {
      display: 'block',
      fontWeight: 600,
      marginBottom: '0.5rem',
    };

    /* eslint-disable react/no-unescaped-entities */
    const tourSteps: ReactourStep[] = [
      {
        selector: '[data-focusid="container"]',
        content: (
          <>
            <b style={stepTitleStyle}>
              A nova correção Letrus está mais completa! Neste tutorial, você
              aprenderá que:
            </b>

            <ul style={{listStyle: 'none', padding: '0.5rem'}}>
              <li>✅ Verde destaca seus pontos fortes.</li>

              <li>🔴 Vermelho indica o que melhorar.</li>

              <li>⚠️ Amarelo sinaliza pontos de atenção.</li>
            </ul>

            <b style={stepTitleStyle}>
              Se houver empate, escolheremos a competência mais relevante para
              focar.
            </b>
          </>
        ),
      },
      {
        selector: isMobile
          ? '[data-focusid="bottomMenu"]'
          : '[data-focusid="competence1"]',
        content: (
          <>
            <b style={stepTitleStyle}>COMENTÁRIOS E COMPETÊNCIAS</b>
            <p>
              Ao clicar em cada competência, ela se abre. Dentro de cada uma,
              você terá acesso à comentários personalizados sobre seu texto.
              Descubra seus pontos fortes e o que precisa ser aprimorado.
            </p>
          </>
        ),
      },
      {
        selector: isMobile
          ? '[data-focusid="competenceCard"]'
          : '[data-focusid="competence0"]',
        content: (
          <>
            <b style={stepTitleStyle}>VER MAIS DETALHES</b>
            <p>
              O botão "Ver mais" mostra explicações detalhadas sobre cada
              comentário.
            </p>
          </>
        ),
      },
      {
        selector: '[data-focusid="editor"]',
        content: (
          <>
            <b style={stepTitleStyle}>MARCAÇÕES DE TEXTO</b>
            <p>
              Ao acessar cada competência, verifique se há marcações no seu
              texto{isMobile ? '' : ' à direita'}. Elas destacam trechos
              relacionados aos comentários, auxiliando na sua análise.
            </p>
          </>
        ),
      },
      {
        selector: '[data-focusid="exitButton"]',
        content: (
          <>
            <b style={stepTitleStyle}>SAIR E DEFINIR METAS</b>
            <p>
              Após analisar seus resultados, clique em "Sair" para definir um
              alvo de melhoria para sua próxima escrita (dentro deste mesmo
              gênero textual) focado em uma competência que você não foi tão
              bem.
            </p>
          </>
        ),
        style: {
          maxWidth: '28rem',
        },
      },
    ];

    return tourSteps;
  }

  const comments =
    getWebSocketComments() ?? httpResponse?.review?.comments_and_indicators;

  const textMarkers =
    getWebSocketMarkers() ?? httpResponse?.review?.text_markers?.json_metadatas;

  const totalScore =
    getWebSocketGrades()?.grade ?? httpResponse?.review?.grade?.grade;

  const difference =
    getWebSocketGrades()?.difference ?? httpResponse?.review?.grade?.difference;

  const comparisonResult =
    getWebSocketGrades()?.comparison_result ??
    httpResponse?.review?.grade?.comparison_result;

  const indicators =
    getWebSocketIndicators() ??
    getIndicators(httpResponse?.review?.text_markers?.numeric_metadatas);

  const annotations = httpResponse?.review?.annotations ?? []; // annotations infos is not send on websocket

  return {
    updateCommentsReviewed,
    getButtonText,
    getWebSocketZeroGrader,
    getComparativeFeedback,
    getWebSocketComments,
    getEditorMarkers,
    getWebSocketFinish,
    getMarkerColor,
    getGreaterMarkers,
    getGreaterCompetence,
    getSmallerCompetence,
    getSmallerMarkers,
    getWebSocketMarkers,
    getWebSocketGrades,
    getShownStats,
    getCurrentComments,
    getTourSteps,
    comments,
    textMarkers,
    totalScore,
    difference,
    comparisonResult,
    annotations,
    indicators,
  };
}

export default useNewReviewed;
